import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from 'lucide-react';
import { describeEstimate, formatPhotoClock, formatSpeedFactor, frameIndexAtProgress } from '../utils/estimateFormat';

const LEVEL_STYLES = {
  ok: { box: 'bg-green-50 border-green-200 text-green-800', Icon: CheckCircle2, icon: 'text-green-600' },
  info: { box: 'bg-blue-50 border-blue-200 text-blue-800', Icon: Info, icon: 'text-blue-600' },
  warning: { box: 'bg-amber-50 border-amber-300 text-amber-900', Icon: AlertTriangle, icon: 'text-amber-600' },
  error: { box: 'bg-red-50 border-red-200 text-red-800', Icon: XCircle, icon: 'text-red-500' },
};

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    const onChange = (e) => setReduced(e.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
};

const GENERATE_BUTTON_CLASS =
  'px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * Pre-creation simulation (no video): plays back the real photos the backend selected. Purely index-based over
 * `frameTimestamps`: progress p -> photo k = min(n - 1, floor(p * n)); the clock jumps photo by photo while the bar
 * advances smoothly over the estimated video duration.
 */
export const TimelapsePreview = ({ estimate, disabled = false }) => {
  const reducedMotion = usePrefersReducedMotion();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | playing | done
  const frameRef = useRef(null);

  const timestamps = estimate.frameTimestamps || [];
  const n = timestamps.length;
  const durationMs = estimate.actualDurationSeconds * 1000;

  // Back to idle whenever the result changes or becomes outdated (stops any running animation).
  useEffect(() => {
    setStatus('idle');
    setProgress(0);
  }, [estimate, disabled]);

  useEffect(() => {
    if (status !== 'playing') return undefined;
    if (reducedMotion || !(durationMs > 0)) {
      setProgress(1);
      setStatus('done');
      return undefined;
    }
    let origin = null;
    const tick = (now) => {
      if (origin === null) origin = now;
      const p = Math.min(1, (now - origin) / durationMs);
      setProgress(p);
      if (p >= 1) {
        setStatus('done');
        return;
      }
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [status, reducedMotion, durationMs]);

  const handleToggle = () => {
    if (status === 'playing') {
      setStatus('idle');
      setProgress(0);
    } else {
      setProgress(0);
      setStatus('playing');
    }
  };

  const buttonLabel = status === 'playing' ? 'Detener' : status === 'done' ? 'Reiniciar' : 'Simular video';
  const percent = Math.round(progress * 1000) / 10;
  const k = frameIndexAtProgress(progress, n);

  return (
    <div className="mt-3 p-3 bg-white border border-gray-200 rounded-lg space-y-2" data-testid="timelapse-preview">
      <p className="text-xs font-semibold text-gray-600">Vista previa de la velocidad (no es el video real)</p>
      <p className="text-lg font-mono font-semibold text-gray-800" aria-live="off">
        {k >= 0 ? formatPhotoClock(timestamps[k]) : formatPhotoClock(estimate.periodStart)}
      </p>
      {n > 0 && (
        <p className="text-xs text-gray-600" data-testid="timelapse-frame-counter">
          Foto {k + 1} de {n}
        </p>
      )}
      <div className="relative h-2 rounded-full bg-gray-200" role="img" aria-label="Línea de tiempo del período">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-blue-500"
          style={{ width: `${percent}%` }}
          data-testid="timelapse-progress"
        />
        <div
          className="absolute top-1/2 w-3.5 h-3.5 -mt-[7px] -ml-[7px] rounded-full bg-blue-600 border-2 border-white shadow"
          style={{ left: `${percent}%` }}
        />
      </div>
      <div className="flex justify-between text-[11px] text-gray-500">
        <span>{formatPhotoClock(estimate.periodStart)}</span>
        <span>{formatPhotoClock(estimate.periodEnd)}</span>
      </div>
      <p className="text-xs text-gray-700">
        1 segundo de video = <span className="font-semibold">{formatSpeedFactor(estimate.speedFactor)}</span> reales
      </p>
      <button type="button" onClick={handleToggle} disabled={disabled} className={GENERATE_BUTTON_CLASS}>
        {buttonLabel}
      </button>
      {estimate.detectedPhotoIntervalMinutes != null && (
        <p className="text-[11px] text-gray-500" data-testid="detected-cadence">
          Cámara con ingreso detectado: 1 foto cada {estimate.detectedPhotoIntervalMinutes} min
        </p>
      )}
      <p className="text-[11px] text-gray-500">Fotos disponibles en el período: {estimate.availablePhotos}</p>
      {reducedMotion && (
        <p className="text-[11px] text-gray-500">La animación está desactivada por tu preferencia de movimiento reducido.</p>
      )}
    </div>
  );
};

const EditorEstimatePanel = ({ estimate, loading, error, stale, canGenerate, onGenerate, onCancel, requestedDurationSeconds }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-600">
        <Loader2 className="w-4 h-4 animate-spin" /> Generando simulación...
        <button type="button" onClick={onCancel} className="ml-auto px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">
          Cancelar
        </button>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-2">
        {error && <p className="text-xs text-amber-800">{error}</p>}
        {!error && (
          <p className="text-xs text-gray-600">
            Simulá cómo se vería el video con las fotos reales de tu cámara antes de crearlo.
          </p>
        )}
        {!canGenerate && <p className="text-[11px] text-gray-500">Completá el período para generar la simulación.</p>}
        <button type="button" onClick={onGenerate} disabled={!canGenerate} className={GENERATE_BUTTON_CLASS}>
          Generar simulación
        </button>
      </div>
    );
  }

  const { level, message } = describeEstimate(estimate, requestedDurationSeconds);
  const { box, Icon, icon } = LEVEL_STYLES[level];

  return (
    <div className="space-y-2">
      {stale && (
        <p className="text-xs font-semibold text-amber-800" data-testid="estimate-stale-label">
          Simulación desactualizada: cambiaste la configuración después de generarla.
        </p>
      )}
      <div
        className={`p-4 border rounded-xl ${box} ${level === 'warning' ? 'border-2' : ''} ${stale ? 'opacity-50' : ''}`}
        role={level === 'warning' ? 'alert' : 'status'}
      >
        <div className="flex items-start gap-2">
          <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${icon}`} />
          <div className="space-y-1 min-w-0">
            {level === 'warning' && <p className="text-sm font-bold">Ajuste automático del video</p>}
            <p className="text-xs md:text-sm">{message}</p>
          </div>
        </div>
        {estimate.status !== 'NO_FRAMES' && <TimelapsePreview estimate={estimate} disabled={stale} />}
      </div>
      {error && <p className="text-xs text-amber-800">{error}</p>}
      {stale && (
        <button type="button" onClick={onGenerate} disabled={!canGenerate} className={GENERATE_BUTTON_CLASS}>
          Regenerar simulación
        </button>
      )}
    </div>
  );
};

export default EditorEstimatePanel;
